import { useEffect, useState } from 'react'
import { useStores } from './useStores'
import { firebaseMessagingService } from 'services/firebaseMessaging.service'
import { fcmTokenManager } from 'services/fcmTokenManager.service'

export const useFirebaseMessaging = () => {
  const { authStore } = useStores()
  const [isSupported, setIsSupported] = useState<boolean>(false)
  const [permissionStatus, setPermissionStatus] = useState<NotificationPermission>('default')
  const [fcmToken, setFcmToken] = useState<string | null>(null)
  const [isConnected, setIsConnected] = useState<boolean>(false)

  useEffect(() => {
    // Check FCM support
    const checkSupport = async () => {
      const supported = await fcmTokenManager.checkFCMSupport()
      setIsSupported(supported)
      
      if (supported && 'Notification' in window) {
        setPermissionStatus(Notification.permission)
      }
    }

    checkSupport()
  }, [])

  useEffect(() => {
    // Update FCM token from auth store
    setFcmToken(authStore.fcmToken || null)
    setIsConnected(!!authStore.fcmToken && !!authStore.userDetail?.id)
  }, [authStore.fcmToken, authStore.userDetail?.id])

  useEffect(() => {
    // This hook can be used by components that need to ensure Firebase messaging is active
    // The service is already started in App.tsx, but this provides a way for components
    // to explicitly ensure messaging is working
    const initializeMessaging = async () => {
      if (authStore.userDetail?.id && isSupported) {
        await firebaseMessagingService.startListening()
      }
    }

    initializeMessaging()
  }, [authStore.userDetail?.id, isSupported])

  const requestPermission = async (): Promise<boolean> => {
    if (!isSupported) return false

    try {
      const permission = await Notification.requestPermission()
      setPermissionStatus(permission)
      return permission === 'granted'
    } catch (error) {
      console.error('Error requesting notification permission:', error)
      return false
    }
  }

  const refreshToken = async (): Promise<void> => {
    if (authStore.userDetail?.id) {
      await fcmTokenManager.refreshToken(authStore.userDetail.id)
    }
  }

  return {
    isSupported,
    permissionStatus,
    fcmToken,
    isConnected,
    requestPermission,
    refreshToken
  }
} 